import { useMutation, useQuery } from '@apollo/client';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    EDIT_PERSON,
    GET_PERSON,
} from '~/services';

const UserDetail: React.FC = () => {
    const navigate = useNavigate();
    const [t] = useTranslation('teams');
    const [alert, setAlert] = useState<{ type: string, message: string } | null>(null);

    // Estados locales para los valores del formulario
    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const { agentId, idUser } = useParams();

    const { data, loading } = useQuery(GET_PERSON, {
        variables: {
            data: {
                agent_id: agentId,
                owner_id: idUser,
            },
        },
    });



    useEffect(() => {
        if (data) {
            const user = data.response.data;

            const firstNameAttribute = user.personAttributes.find((attr: any) => attr.field_name === 'first_name');
            const phoneNumberAttribute = user.personAttributes.find((attr: any) => attr.field_name === 'billing_phone');

            setName(firstNameAttribute ? firstNameAttribute.value : '');
            setRole(user.role || '');
            setPhone(phoneNumberAttribute ? phoneNumberAttribute.value : '');
            setEmail(user.email || '');
        }
    }, [data]);

    const [editPerson] = useMutation(EDIT_PERSON);
    const handleSave = async () => {
        try {
            await editPerson({
                variables: {
                    data: {
                        agent_id: agentId,
                        person_id: data.response.data.person_id,
                        role,
                        personAttributes: [
                            {
                                field_name: 'first_name',
                                value: name,
                            },
                            {
                                field_name: 'billing_phone',
                                value: phone,
                            },
                        ],
                    },
                },
            });
            setAlert({ type: 'success', message: t('save_success') });
        } catch (error) {
            setAlert({ type: 'error', message: t('save_error') });
        }
        setTimeout(() => setAlert(null), 3000);
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <div className=" ">
            {alert && (
                <div className={`alert alert-${alert.type} p-4 mb-4 text-sm text-${alert.type === 'success' ? 'green' : 'red'}-700 bg-${alert.type === 'success' ? 'green' : 'red'}-100 rounded-lg`} role="alert">
                    {alert.message}
                </div>
            )}
            <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
            >
                <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                {t('back')}
            </button>

            <div className="bg-white p-6 rounded-md shadow-md max-w-4xl mx-auto">
                <div className="flex flex-col lg:flex-row">

                    <div className="flex-1 lg:ml-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('label_name')}
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    className="input border border-gray-300 rounded-md p-3 text-base text-gray-700 w-full"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>

                            <div>
                                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('label_role')}
                                </label>
                                <select
                                    id="role"
                                    className="input border rounded-md p-3 text-base text-gray-700 border-gray-300 w-full"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                >
                                    <option value="">{t('placeholder_select_role')}</option>
                                    <option value="ADMIN">{t('role_admin')}</option>
                                    <option value="EDITOR">{t('role_editor')}</option>
                                    <option value="COLLABORATOR">{t('role_collaborator')}</option>
                                    {/* Agrega más roles según sea necesario */}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('label_phone')}
                                </label>
                                <div className="flex items-center">

                                    <input
                                        type="text"
                                        id="phone"
                                        className="input border border-gray-300 rounded-md p-3 text-base text-gray-700 w-full"
                                        value={phone.replace('+57 ', '')}
                                        onChange={(e) => setPhone(`+57 ${e.target.value}`)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('label_email')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    className="input border border-gray-300 rounded-md p-3 text-base text-gray-700 w-full"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    readOnly
                                />
                            </div>
                        </div>

                        <p className="text-sm text-gray-500 mb-4">
                            {t('email_info')}
                        </p>

                        <div className="flex justify-end">
                            <button
                                onClick={handleSave}
                                className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
                            >
                                {t('save')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetail;
